import { z } from 'zod';
import { amountSchema } from '../common/amount.js';
import { paginationMetaSchema } from '../common/pagination.js';
import { employeeRefSchema } from '../common/refs.js';
import { accountNameSchema, accountSchema, accountTotalsSchema, debtLimitInputSchema, } from './accounts.js';
import { balanceStatusSchema, exportLocaleShape, linkStatusSchema, pageQueryShape, periodIsOrdered, periodQueryShape, PERIOD_ORDER_ISSUE, } from './common.js';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
import { debtorNameIssue, debtorProfileIssueSchema } from './profile.js';
/**
 * عملاء الذمم (debts.md section 6): a debtor (عميل الذمم) is one person with
 * one phone, unique among debtors, holding one or more accounts. A debtor is
 * never deleted; a user sees it through its accounts that are not deleted in
 * the user's branches, and its balance adds up only those accounts.
 */
export const DEBTOR_NAME_MIN = 2;
export const DEBTOR_NAME_MAX = 150;
/**
 * اسم العميل: trimmed, inner spaces collapsed, 2-150 characters with at least
 * one Arabic or Latin letter (debts.md section 22.1), otherwise free text.
 */
export const debtorNameSchema = z
    .string()
    .transform((v) => v.trim().replace(/\s+/g, ' '))
    .pipe(z
    .string()
    .min(DEBTOR_NAME_MIN)
    .max(DEBTOR_NAME_MAX)
    .refine((v) => debtorNameIssue(v) === null, {
    message: 'must contain Arabic or English letters',
    params: { code: 'NAME_INVALID' },
}));
/**
 * The verification id of a save that needs a WhatsApp code (debts.md section
 * 21, customers.md section 10.4): absent the first time; the API then answers
 * PHONE_VERIFICATION_REQUIRED when the switch is on and the phone needs one.
 */
export const phoneVerificationIdSchema = z.string().trim().min(1).max(64);
/**
 * إضافة عميل: the debtor and its first account in one save (debts.md section
 * 6.2). The phone follows the shared rule (customers.md section 9): a mobile
 * only, stored in E.164 whatever form it was typed in.
 */
export const createDebtorSchema = z.object({
    name: debtorNameSchema,
    phone: phoneSchema,
    accountName: accountNameSchema,
    branchId: z.string().trim().min(1),
    debtLimit: debtLimitInputSchema.optional(),
    phoneVerificationId: phoneVerificationIdSchema.optional(),
});
/**
 * تعديل العميل: اسم العميل and رقم الهاتف only; no balance is ever edited here.
 * The phone is read with `storedPhoneSchema`: a stored phone of an excluded
 * country saves unchanged, and the service refuses it with
 * PHONE_COUNTRY_NOT_ALLOWED when it changed (customers.md section 9.7).
 */
export const updateDebtorSchema = z
    .object({
    name: debtorNameSchema.optional(),
    phone: storedPhoneSchema.optional(),
    phoneVerificationId: phoneVerificationIdSchema.optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.phone !== undefined, {
    message: 'at least one field is required',
});
/**
 * إعادة التحقق (customers.md section 10.4, debts.md section 21): the same
 * phone verified again after Customers refused the stored receipt. The
 * verification id is required while the switch is on.
 */
export const reverifyDebtorPhoneSchema = z
    .object({ phoneVerificationId: phoneVerificationIdSchema.optional() })
    .strict();
/**
 * `error.data` of `PHONE_EXISTS`: the debtor that holds the phone, by id and
 * name only, so the drawer can offer إضافة حساب لهذا العميل even when none of
 * its accounts is in the user's branches.
 */
export const phoneExistsDataSchema = z.object({ debtorId: z.string(), name: z.string() });
/** Newest first by default; sortable by name, balance and creation date. */
export const DEBTOR_SORT_FIELDS = ['createdAt', 'name', 'balance'];
const debtorFilterShape = {
    /**
     * Debtor name or phone (contains), or, when the text is a number, the
     * debtor holding the account with that number.
     */
    search: z.string().trim().max(200).optional(),
    /** Debtors with an account in that branch; their totals then add only that branch. */
    branchId: z.string().trim().min(1).optional(),
    /** On the debtor's total over the shown accounts. */
    balanceStatus: balanceStatusSchema.optional(),
    linkStatus: linkStatusSchema.optional(),
    /** On the debtor's creation date. */
    ...periodQueryShape,
    sortBy: z.enum(DEBTOR_SORT_FIELDS).default('createdAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
};
export const debtorListQuerySchema = z
    .object({ ...debtorFilterShape, ...pageQueryShape })
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
/** تصدير إلى Excel of the list: one row per shown account. */
export const debtorExportQuerySchema = z
    .object({
    ...debtorFilterShape,
    ...exportLocaleShape,
    format: z.literal('xlsx').default('xlsx'),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
/**
 * The visible error of a final Customers refusal after a verified save
 * (customers.md section 10.4): PHONE_REJECTED «رفض نظام العملاء هذا الرقم،
 * عدّل رقم الهاتف»; VERIFICATION_EXPIRED «انتهت صلاحية تأكيد الرقم، أعد
 * التحقق» with the action إعادة التحقق.
 */
export const DEBTOR_LINK_ERRORS = ['PHONE_REJECTED', 'VERIFICATION_EXPIRED'];
export const debtorLinkErrorSchema = z.enum(DEBTOR_LINK_ERRORS);
export const debtorSchema = z.object({
    id: z.string(),
    name: z.string(),
    phone: z.string().nullable(),
    /** رقم مؤكد: Customers applied a verification receipt for this phone. */
    phoneVerified: z.boolean(),
    /**
     * The legacy phone text of the debtor's first shown migrated account, for a
     * debtor without a phone; null otherwise.
     */
    legacyPhone: z.string().nullable(),
    customerId: z.string().nullable(),
    linkStatus: linkStatusSchema,
    linkError: debtorLinkErrorSchema.nullable(),
    /** بيانات العميل غير مكتملة: empty when the name and phone are complete (debts.md section 22.1). */
    profileIssues: z.array(debtorProfileIssueSchema),
    /** True when the debtor was formed by the legacy migration (no creator). */
    migrated: z.boolean(),
    /** Null for migrated debtors ("—"). */
    createdBy: employeeRefSchema.nullable(),
    /** Null when legacy had no creation date ("—"). */
    createdAt: z.string().nullable(),
    updatedAt: z.string(),
});
/** A row of the list: the debtor with عدد الحسابات and the sum of the shown accounts. */
export const debtorListRowSchema = debtorSchema.extend({
    accountCount: z.number().int().nonnegative(),
    balance: amountSchema,
});
export const debtorListSchema = z.object({
    items: z.array(debtorListRowSchema),
    meta: paginationMetaSchema,
    /** Over the filtered debtors' totals. */
    totals: accountTotalsSchema,
});
/** The customer page (debts.md section 6.3): header and the الحسابات tab. */
export const debtorPageSchema = z.object({
    debtor: debtorSchema,
    /** الرصيد الإجمالي: the sum of the accounts below. */
    balance: amountSchema,
    /** The debtor's accounts that are not deleted, in the user's branches, by number. */
    accounts: z.array(accountSchema),
});
export const createDebtorResultSchema = z.object({
    debtor: debtorSchema,
    account: accountSchema,
});
/**
 * نقل الحسابات إلى العميل المسجل (debts.md section 22.4): every account of an
 * incomplete debtor moves under the debtor that holds `phone`. `accounts`
 * names each of its accounts in the user's branches, with the name it takes
 * under the target (unique per branch, section 6.4).
 */
export const mergeDebtorSchema = z
    .object({
    phone: phoneSchema,
    targetDebtorId: z.string().trim().min(1).max(64),
    accounts: z
        .array(z.object({ id: z.string().trim().min(1).max(64), name: accountNameSchema }).strict())
        .min(1)
        .max(500),
})
    .strict();
export const mergeDebtorResultSchema = z.object({
    /** The target, the main profile the accounts now belong to. */
    debtor: debtorSchema,
    movedAccountIds: z.array(z.string()),
});
//# sourceMappingURL=debtors.js.map